import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AiAssistantPanel } from "@/components/ai-assistant-panel";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ErrorBoundary } from "@/components/error-boundary";
import { useDean } from "@/contexts/dean-context";
import { ChevronDown } from "lucide-react";

const DEAN_ACK_KEY = "dean-sla-last-acknowledged";

function useCriticalSlaBreachUnacked(): boolean {
  const { data } = useQuery<{ alerts: Array<{ type: string; severity: string }> }>({
    queryKey: ["/api/alerts"],
    refetchInterval: 30_000,
    staleTime: 20_000,
    retry: false,
  });
  const lastAcked = Number(localStorage.getItem(DEAN_ACK_KEY) ?? "0");
  const hasCritical = data?.alerts?.some(
    (a) => a.type === "data_overdue" && a.severity === "critical",
  ) ?? false;
  const [acknowledged, setAcknowledged] = useState(() => lastAcked > Date.now() - 3600_000);
  useEffect(() => {
    if (!hasCritical) setAcknowledged(false);
  }, [hasCritical]);
  return hasCritical && !acknowledged;
}

function acknowledgeDeanslaAlert() {
  try { localStorage.setItem(DEAN_ACK_KEY, String(Date.now())); } catch { /* ignore */ }
}

const STORAGE_KEY = "dean-position";
const PANEL_SIZE_KEY = "dean-panel-size";
const PANEL_POS_KEY = "dean-panel-position";
const AVATAR_SIZE = 56;
const DEFAULT_PANEL_WIDTH = 480;
const DEFAULT_PANEL_HEIGHT = 560;
const MIN_PANEL_WIDTH = 360;
const MIN_PANEL_HEIGHT = 420;
const EDGE_PADDING = 8;

type PanelSize = { width: number; height: number };
type ResizeDir = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

function getMaxPanelWidth() { return Math.floor(window.innerWidth * 0.9); }
function getMaxPanelHeight() { return Math.floor(window.innerHeight * 0.9); }

function clampPanelSize(w: number, h: number): PanelSize {
  return {
    width: Math.max(MIN_PANEL_WIDTH, Math.min(w, getMaxPanelWidth())),
    height: Math.max(MIN_PANEL_HEIGHT, Math.min(h, getMaxPanelHeight())),
  };
}

function loadPanelSize(): PanelSize {
  try {
    const stored = localStorage.getItem(PANEL_SIZE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as PanelSize;
      return clampPanelSize(parsed.width, parsed.height);
    }
  } catch { /* ignore */ }
  return { width: DEFAULT_PANEL_WIDTH, height: DEFAULT_PANEL_HEIGHT };
}

function savePanelSize(size: PanelSize) {
  try {
    localStorage.setItem(PANEL_SIZE_KEY, JSON.stringify(size));
  } catch { /* ignore */ }
}

type PanelPos = { left: number; top: number };

function clampPanelPos(left: number, top: number, panelW: number, panelH: number): PanelPos {
  return {
    left: Math.max(EDGE_PADDING, Math.min(left, window.innerWidth - panelW - EDGE_PADDING)),
    top: Math.max(EDGE_PADDING, Math.min(top, window.innerHeight - panelH - EDGE_PADDING)),
  };
}

function loadPanelPos(): PanelPos | null {
  try {
    const stored = localStorage.getItem(PANEL_POS_KEY);
    if (stored) {
      const pos = JSON.parse(stored) as PanelPos;
      const size = loadPanelSize();
      return clampPanelPos(pos.left, pos.top, size.width, size.height);
    }
  } catch { /* ignore */ }
  return null;
}

function savePanelPos(pos: PanelPos) {
  try {
    localStorage.setItem(PANEL_POS_KEY, JSON.stringify(pos));
  } catch { /* ignore */ }
}

function getDefaultPosition() {
  return {
    x: window.innerWidth - AVATAR_SIZE - EDGE_PADDING - 16,
    y: window.innerHeight - AVATAR_SIZE - EDGE_PADDING - 16,
  };
}

function loadPosition(): { x: number; y: number } {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const pos = JSON.parse(stored) as { x: number; y: number };
      const maxX = window.innerWidth - AVATAR_SIZE - EDGE_PADDING;
      const maxY = window.innerHeight - AVATAR_SIZE - EDGE_PADDING;
      return {
        x: Math.max(EDGE_PADDING, Math.min(pos.x, maxX)),
        y: Math.max(EDGE_PADDING, Math.min(pos.y, maxY)),
      };
    }
  } catch {
    // ignore
  }
  return getDefaultPosition();
}

function savePosition(pos: { x: number; y: number }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

function DeanAvatarBubble({ onMouseDown, onTouchStart, onClick, showAlert }: {
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onClick: () => void;
  showAlert?: boolean;
}) {
  const { t } = useTranslation(["dean", "common"]);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        aria-label={t("dean:widget.open")}
        data-testid="dean-floating-avatar"
        className="h-11 px-4 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-xl hover:shadow-2xl text-white font-bold text-sm select-none border-2 border-white/10 hover:scale-105 active:scale-95 transition-all duration-150 gap-1.5"
      >
        <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold shrink-0">D</span>
        Dean
      </button>
      {showAlert && (
        <span
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 animate-pulse shadow-lg ring-2 ring-white dark:ring-slate-900"
          data-testid="dean-sla-alert-dot"
          aria-label={t("dean:widget.criticalAlert")}
        />
      )}
    </div>
  );
}

const RESIZE_HANDLES: Array<{
  dir: ResizeDir;
  className: string;
  style: React.CSSProperties;
}> = [
  {
    dir: "n",
    className: "cursor-ns-resize",
    style: { top: 0, left: 8, right: 8, height: 6, position: "absolute" },
  },
  {
    dir: "s",
    className: "cursor-ns-resize",
    style: { bottom: 0, left: 8, right: 8, height: 6, position: "absolute" },
  },
  {
    dir: "e",
    className: "cursor-ew-resize",
    style: { top: 8, bottom: 8, right: 0, width: 6, position: "absolute" },
  },
  {
    dir: "w",
    className: "cursor-ew-resize",
    style: { top: 8, bottom: 8, left: 0, width: 6, position: "absolute" },
  },
  {
    dir: "ne",
    className: "cursor-nesw-resize",
    style: { top: 0, right: 0, width: 12, height: 12, position: "absolute" },
  },
  {
    dir: "nw",
    className: "cursor-nwse-resize",
    style: { top: 0, left: 0, width: 12, height: 12, position: "absolute" },
  },
  {
    dir: "se",
    className: "cursor-nwse-resize",
    style: { bottom: 0, right: 0, width: 12, height: 12, position: "absolute" },
  },
  {
    dir: "sw",
    className: "cursor-nesw-resize",
    style: { bottom: 0, left: 0, width: 12, height: 12, position: "absolute" },
  },
];

export function DraggableDean() {
  const { t } = useTranslation(["dean", "common"]);
  const { requestedConversationId, isOpen: contextOpen, setIsOpen: setContextOpen, clearRequestedConversation, newChatRevision: newChatKey } = useDean();
  const showAlertDot = useCriticalSlaBreachUnacked();
  const [position, setPosition] = useState<{ x: number; y: number }>(loadPosition);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelExpanded, setPanelExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const [panelSize, setPanelSize] = useState<PanelSize>(loadPanelSize);

  const panelExpandedRef = useRef(panelExpanded);
  panelExpandedRef.current = panelExpanded;

  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);
  const positionRef = useRef(position);
  positionRef.current = position;

  const panelRef = useRef<HTMLDivElement>(null);
  const panelSizeRef = useRef(panelSize);
  panelSizeRef.current = panelSize;

  const isResizing = useRef(false);
  const resizeDir = useRef<ResizeDir>("e");
  const resizeStart = useRef({ x: 0, y: 0 });
  const resizePanelStart = useRef({ width: DEFAULT_PANEL_WIDTH, height: DEFAULT_PANEL_HEIGHT, left: 0, top: 0 });
  const [resizePosOverride, setResizePosOverride] = useState<PanelPos | null>(null);
  const resizePosOverrideRef = useRef<PanelPos | null>(null);

  const [panelPos, setPanelPos] = useState<PanelPos | null>(loadPanelPos);
  const panelPosRef = useRef(panelPos);
  panelPosRef.current = panelPos;
  const isPanelDragging = useRef(false);
  const panelDragOffset = useRef({ x: 0, y: 0 });

  const isDragExpanding = useRef(false);
  const dragExpandStartY = useRef(0);
  const [dragExpandLiveHeight, setDragExpandLiveHeight] = useState<number | null>(null);

  // Sync context-driven open/close with local state
  useEffect(() => {
    if (contextOpen && !panelOpen) {
      setPanelOpen(true);
      setPanelExpanded(true);
    } else if (!contextOpen && panelOpen) {
      setPanelOpen(false);
    }
  }, [contextOpen]);

  // Sync local close back to context
  useEffect(() => {
    if (!panelOpen && contextOpen) {
      setContextOpen(false);
    }
  }, [panelOpen]);

  // Step 3: re-clamp panel position when expanding from collapsed state
  useEffect(() => {
    if (!panelExpanded) return;
    setPanelPos((prev) => {
      if (!prev) return prev;
      const w = panelSizeRef.current.width;
      const h = panelSizeRef.current.height;
      const clamped = clampPanelPos(prev.left, prev.top, w, h);
      if (clamped.left === prev.left && clamped.top === prev.top) return prev;
      savePanelPos(clamped);
      return clamped;
    });
  }, [panelExpanded]);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 640);
      setPosition((prev) => {
        const maxX = window.innerWidth - AVATAR_SIZE - EDGE_PADDING;
        const maxY = window.innerHeight - AVATAR_SIZE - EDGE_PADDING;
        return {
          x: Math.max(EDGE_PADDING, Math.min(prev.x, maxX)),
          y: Math.max(EDGE_PADDING, Math.min(prev.y, maxY)),
        };
      });
      // Step 5: compute clamped size fresh (not from stale ref) then clamp pos
      setPanelPos((prev) => {
        const s = clampPanelSize(panelSizeRef.current.width, panelSizeRef.current.height);
        if (!prev) return null;
        return clampPanelPos(prev.left, prev.top, s.width, s.height);
      });
      setPanelSize((prev) => clampPanelSize(prev.width, prev.height));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const clampPosition = useCallback((x: number, y: number) => {
    const maxX = window.innerWidth - AVATAR_SIZE - EDGE_PADDING;
    const maxY = window.innerHeight - AVATAR_SIZE - EDGE_PADDING;
    return {
      x: Math.max(EDGE_PADDING, Math.min(x, maxX)),
      y: Math.max(EDGE_PADDING, Math.min(y, maxY)),
    };
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (panelOpen) return;
    isDragging.current = true;
    hasMoved.current = false;
    dragOffset.current = {
      x: e.clientX - positionRef.current.x,
      y: e.clientY - positionRef.current.y,
    };
    e.preventDefault();
  }, [panelOpen]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (panelOpen) return;
    isDragging.current = true;
    hasMoved.current = false;
    const touch = e.touches[0];
    dragOffset.current = {
      x: touch.clientX - positionRef.current.x,
      y: touch.clientY - positionRef.current.y,
    };
  }, [panelOpen]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const newRawX = e.clientX - dragOffset.current.x;
      const newRawY = e.clientY - dragOffset.current.y;
      const dx = Math.abs(newRawX - positionRef.current.x);
      const dy = Math.abs(newRawY - positionRef.current.y);
      if (dx > 4 || dy > 4) hasMoved.current = true;
      setPosition(clampPosition(newRawX, newRawY));
    };

    const onMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        savePosition(positionRef.current);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging.current) return;
      const touch = e.touches[0];
      const newRawX = touch.clientX - dragOffset.current.x;
      const newRawY = touch.clientY - dragOffset.current.y;
      const dx = Math.abs(newRawX - positionRef.current.x);
      const dy = Math.abs(newRawY - positionRef.current.y);
      if (dx > 4 || dy > 4) hasMoved.current = true;
      setPosition(clampPosition(newRawX, newRawY));
      e.preventDefault();
    };

    const onTouchEnd = () => {
      if (isDragging.current) {
        isDragging.current = false;
        savePosition(positionRef.current);
      }
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [clampPosition]);

  const handleResizePointerDown = useCallback((e: React.PointerEvent, dir: ResizeDir) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;

    isResizing.current = true;
    resizeDir.current = dir;
    resizeStart.current = { x: e.clientX, y: e.clientY };
    resizePanelStart.current = {
      width: rect.width,
      height: rect.height,
      left: rect.left,
      top: rect.top,
    };

    setResizePosOverride({ left: rect.left, top: rect.top });

    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (!isResizing.current) return;

      const dx = e.clientX - resizeStart.current.x;
      const dy = e.clientY - resizeStart.current.y;
      const { width: startW, height: startH, left: startL, top: startT } = resizePanelStart.current;
      const dir = resizeDir.current;

      let newWidth = startW;
      let newHeight = startH;
      let newLeft = startL;
      let newTop = startT;

      if (dir.includes("e")) {
        // Step 4: cap east so panel right edge stays within viewport
        const maxEastWidth = window.innerWidth - startL - EDGE_PADDING;
        newWidth = Math.min(startW + dx, maxEastWidth);
      }
      if (dir.includes("w")) {
        newWidth = startW - dx;
        newLeft = startL + dx;
      }
      if (dir.includes("s")) {
        // Step 4: cap south so panel bottom edge stays within viewport
        const maxSouthHeight = window.innerHeight - startT - EDGE_PADDING;
        newHeight = Math.min(startH + dy, maxSouthHeight);
      }
      if (dir.includes("n")) {
        newHeight = startH - dy;
        newTop = startT + dy;
      }

      const maxW = getMaxPanelWidth();
      const maxH = getMaxPanelHeight();
      const clampedW = Math.max(MIN_PANEL_WIDTH, Math.min(newWidth, maxW));
      const clampedH = Math.max(MIN_PANEL_HEIGHT, Math.min(newHeight, maxH));

      if (dir.includes("w")) {
        if (clampedW !== newWidth) {
          newLeft = startL + (startW - clampedW);
        }
        newLeft = Math.max(EDGE_PADDING, Math.min(newLeft, window.innerWidth - clampedW - EDGE_PADDING));
      }
      if (dir.includes("n")) {
        if (clampedH !== newHeight) {
          newTop = startT + (startH - clampedH);
        }
        newTop = Math.max(EDGE_PADDING, Math.min(newTop, window.innerHeight - clampedH - EDGE_PADDING));
      }

      if (dir.includes("e")) {
        newLeft = Math.max(EDGE_PADDING, Math.min(newLeft, window.innerWidth - clampedW - EDGE_PADDING));
      }
      if (dir.includes("s")) {
        newTop = Math.max(EDGE_PADDING, Math.min(newTop, window.innerHeight - clampedH - EDGE_PADDING));
      }

      const newPos = { left: newLeft, top: newTop };
      setPanelSize({ width: clampedW, height: clampedH });
      setResizePosOverride(newPos);
      resizePosOverrideRef.current = newPos;
    };

    const onPointerUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      savePanelSize(panelSizeRef.current);
      if (resizePosOverrideRef.current) {
        setPanelPos(resizePosOverrideRef.current);
        savePanelPos(resizePosOverrideRef.current);
        resizePosOverrideRef.current = null;
      }
      setResizePosOverride(null);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, []);

  const handleExpandDragPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragExpanding.current = true;
    dragExpandStartY.current = e.clientY;
    setDragExpandLiveHeight(56);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (!isDragExpanding.current) return;
      const dy = e.clientY - dragExpandStartY.current;
      const upward = -dy;
      const maxH = panelSizeRef.current.height;
      const liveH = Math.max(56, Math.min(56 + upward, maxH));
      setDragExpandLiveHeight(liveH);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!isDragExpanding.current) return;
      isDragExpanding.current = false;
      const dy = e.clientY - dragExpandStartY.current;
      const upward = -dy;
      if (upward >= -10) {
        setPanelExpanded(true);
      }
      setDragExpandLiveHeight(null);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, []);

  const handleHeaderPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    isPanelDragging.current = true;
    panelDragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (!isPanelDragging.current) return;
      const w = panelSizeRef.current.width;
      const h = panelExpandedRef.current ? panelSizeRef.current.height : 56;
      const newLeft = e.clientX - panelDragOffset.current.x;
      const newTop = e.clientY - panelDragOffset.current.y;
      const clamped = clampPanelPos(newLeft, newTop, w, h);
      setPanelPos(clamped);
      panelPosRef.current = clamped;
    };

    const onPointerUp = () => {
      if (!isPanelDragging.current) return;
      isPanelDragging.current = false;
      if (panelPosRef.current) savePanelPos(panelPosRef.current);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, []);


  const handleAvatarClick = useCallback(() => {
    if (hasMoved.current) return;
    acknowledgeDeanslaAlert();
    setPanelOpen((v) => !v);
    setPanelExpanded(true);
  }, []);

  const handleClose = useCallback(() => {
    setPanelOpen(false);
    setContextOpen(false);
    clearRequestedConversation();
  }, [setContextOpen, clearRequestedConversation]);

  const getPanelStyle = (): React.CSSProperties => {
    const w = panelSize.width;
    const h = panelSize.height;
    const currentHeight = dragExpandLiveHeight !== null ? dragExpandLiveHeight : (panelExpanded ? h : 56);
    const isLiveDragging = dragExpandLiveHeight !== null;

    if (resizePosOverride) {
      return {
        position: "fixed",
        zIndex: 199,
        pointerEvents: "auto",
        width: w,
        height: currentHeight,
        left: resizePosOverride.left,
        top: resizePosOverride.top,
        userSelect: "none",
      };
    }

    if (panelPos) {
      const clamped = clampPanelPos(panelPos.left, panelPos.top, w, currentHeight);
      return {
        position: "fixed",
        zIndex: 199,
        pointerEvents: "auto",
        width: w,
        height: currentHeight,
        left: clamped.left,
        top: clamped.top,
        transition: isLiveDragging ? "none" : "height 0.2s ease",
        userSelect: isPanelDragging.current ? "none" : undefined,
      };
    }

    const spaceBelow = window.innerHeight - position.y - AVATAR_SIZE;
    const spaceAbove = position.y;
    const spaceRight = window.innerWidth - position.x;
    const spaceLeft = position.x + AVATAR_SIZE;

    const style: React.CSSProperties = {
      position: "fixed",
      zIndex: 199,
      pointerEvents: "auto",
      width: w,
      height: currentHeight,
      transition: isLiveDragging ? "none" : "height 0.2s ease",
    };

    if (spaceBelow >= h + 16) {
      style.top = position.y + AVATAR_SIZE + 8;
    } else if (spaceAbove >= h + 16) {
      style.top = position.y - h - 8;
    } else {
      style.top = Math.max(EDGE_PADDING, Math.min(
        position.y + AVATAR_SIZE / 2 - h / 2,
        window.innerHeight - h - EDGE_PADDING,
      ));
    }

    if (spaceRight >= w + 16) {
      style.left = position.x;
    } else if (spaceLeft >= w + 16) {
      style.left = position.x + AVATAR_SIZE - w;
    } else {
      style.left = Math.max(EDGE_PADDING, window.innerWidth - w - EDGE_PADDING);
    }

    // Step 2: terminal clamp to ensure auto-placed panel never escapes viewport
    const finalClamped = clampPanelPos(style.left as number, style.top as number, w, currentHeight);
    style.left = finalClamped.left;
    style.top = finalClamped.top;

    return style;
  };

  if (isMobile) {
    return (
      <>
        {!panelOpen && (
          <div
            className="fixed bottom-5 right-5 z-[200]"
            data-testid="dean-mobile-trigger"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                type="button"
                onClick={() => { acknowledgeDeanslaAlert(); setPanelOpen(true); }}
                aria-label={t("dean:widget.open")}
                data-testid="dean-floating-avatar"
                className="h-11 px-4 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-xl hover:shadow-2xl text-white font-bold text-sm select-none border-2 border-white/10 hover:scale-105 active:scale-95 transition-all duration-150 gap-1.5"
              >
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold shrink-0">D</span>
                Dean
              </button>
              {showAlertDot && (
                <span
                  className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 animate-pulse shadow-lg ring-2 ring-white dark:ring-slate-900"
                  data-testid="dean-sla-alert-dot"
                  aria-label={t("dean:widget.criticalAlert")}
                />
              )}
            </div>
          </div>
        )}
        <Sheet open={panelOpen} onOpenChange={(open) => { setPanelOpen(open); if (!open) { setContextOpen(false); clearRequestedConversation(); } }}>
          <SheetContent
            side="bottom"
            className="p-0 h-[85vh] rounded-t-2xl overflow-hidden"
            data-testid="dean-mobile-sheet"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <div className="h-full" data-dean-panel="true">
              <ErrorBoundary label={t("dean:widget.aiAssistant")}>
                <AiAssistantPanel key={newChatKey} open={panelOpen} onClose={handleClose} conversationId={requestedConversationId} />
              </ErrorBoundary>
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <>
      {!panelOpen && (
        <div
          style={{ position: "fixed", left: position.x, top: position.y, zIndex: 200, pointerEvents: "auto" }}
          className="select-none cursor-grab active:cursor-grabbing"
          data-testid="dean-draggable-container"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <DeanAvatarBubble
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            onClick={handleAvatarClick}
            showAlert={showAlertDot}
          />
        </div>
      )}

      {panelOpen && (
        <div
          ref={panelRef}
          className="shadow-2xl rounded-2xl overflow-visible border bg-background flex flex-col"
          style={getPanelStyle()}
          data-testid="dean-floating-panel"
          data-dean-panel="true"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {RESIZE_HANDLES.map(({ dir, className, style }) => (
            <div
              key={dir}
              className={`z-10 ${className}`}
              style={style}
              onPointerDown={(e) => handleResizePointerDown(e, dir)}
              data-testid={`dean-resize-handle-${dir}`}
            />
          ))}

          {!panelExpanded && (
            <>
              <div
                className="absolute left-0 right-0 flex justify-center items-start pt-1.5 z-30 cursor-n-resize group"
                style={{ top: 0, height: 18 }}
                onPointerDown={handleExpandDragPointerDown}
                onClick={() => setPanelExpanded(true)}
                data-testid="dean-expand-drag-handle-top"
                aria-label={t("dean:widget.expandHint")}
              >
                <div className="w-10 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-muted-foreground/70 transition-colors" />
              </div>
              <div
                className="absolute left-0 right-0 flex justify-center items-end pb-1.5 z-30 cursor-n-resize group"
                style={{ bottom: 0, height: 18 }}
                onPointerDown={handleExpandDragPointerDown}
                onClick={() => setPanelExpanded(true)}
                data-testid="dean-expand-drag-handle-bottom"
                aria-label={t("dean:widget.expandHint")}
              >
                <div className="w-10 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-muted-foreground/70 transition-colors" />
              </div>
            </>
          )}

          <div
            className="flex items-center gap-2.5 px-3 py-2.5 border-b bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900/60 dark:to-slate-800/60 shrink-0 rounded-t-2xl overflow-hidden cursor-move select-none"
            onPointerDown={handleHeaderPointerDown}
            onDoubleClick={() => { if (!panelExpanded) setPanelExpanded(true); }}
            data-testid="dean-panel-header"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white text-xs font-bold shrink-0">
              D
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight">DEAN</p>
              <p className="text-[10px] text-muted-foreground leading-tight">{t("dean:subtitle")}</p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded"
              data-testid="dean-panel-close"
              aria-label={t("dean:widget.close")}
            >
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          {(panelExpanded || dragExpandLiveHeight !== null) && (
            <div className="flex-1 min-h-0 overflow-hidden rounded-b-2xl">
              <ErrorBoundary label={t("dean:widget.aiAssistant")}>
                <AiAssistantPanel key={newChatKey} open={panelOpen} onClose={handleClose} conversationId={requestedConversationId} />
              </ErrorBoundary>
            </div>
          )}
        </div>
      )}
    </>
  );
}
